import { useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Group, Modal,
  MultiSelect, NumberInput, Select, Stack, Switch, Table, TextInput,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'
import {
  IconAlertTriangle, IconPlus, IconTrash, IconUserPlus, IconUsers,
} from '@tabler/icons-react'
import { DataCard, EmptyState } from '@/components/ui'
import { useCan } from '@/store/auth'
import { useAlertTeams, useTeamMutations } from '@/hooks/useAlerts'
import { api } from '@/api/client'
import { selectableUsers, type MixedUserRow } from '@/utils/realUsers'

const ENVS = ['DEV', 'HML', 'QA', 'PRD', 'BOX', 'VPN', 'OLD']

const SEV_OPTIONS = [
  { value: '0', label: 'Tudo (info+)' },
  { value: '1', label: 'Warning' },
  { value: '2', label: 'High/Disaster' },
  { value: '3', label: 'Fatal' },
]

interface UserItem extends MixedUserRow {
  id: number | null
  email: string
  full_name: string
}

export function AlertTeamsAdmin() {
  const can = useCan()
  if (!can('alerts', 'manage')) {
    return (
      <Alert color="yellow" title="Acesso restrito" icon={<IconAlertTriangle size={18} />}>
        Você não possui permissão para gerenciar Times de alerta.
      </Alert>
    )
  }
  return <AlertTeamsAdminInner />
}

function AlertTeamsAdminInner() {
  const { data: teams, isLoading } = useAlertTeams()
  const m = useTeamMutations()

  // --- Estado: criar time ---
  const [createOpen, createCtl] = useDisclosure(false)
  const [name, setName] = useState('')

  // --- Estado: cobertura ---
  const [coverageForId, setCoverageForId] = useState<number | null>(null)
  const [cv, setCv] = useState({
    client: '',
    environment: 'PRD',
    severity_min: '2',
    channels: ['feed', 'webpush'],
    notify_resolved: false,
  })

  // --- Estado: adicionar membro ---
  const [memberForId, setMemberForId] = useState<number | null>(null)
  const [memberUserId, setMemberUserId] = useState<number | ''>('')

  // Busca lista de usuários para o modal de membro
  const { data: usersData } = useQuery<{ items: UserItem[] }>({
    queryKey: ['users-list-for-teams'],
    queryFn: () => api.get('/v1/users').then((r) => r.data),
    enabled: memberForId !== null,
    staleTime: 120_000,
  })
  // `/v1/users` mistura cadastros PENDENTES (`id: null`) — ver
  // utils/realUsers. Dois deles gerariam duas opcoes de value "null" e o
  // Mantine derruba a tela.
  const userOptions = selectableUsers(usersData?.items ?? []).map((u) => ({
    value: String(u.id),
    label: `${u.full_name} (${u.email})`,
  }))

  return (
    <Stack gap="md">
      <DataCard flush
        title="Times de alerta"
        loading={isLoading}
        extra={
          <Button leftSection={<IconPlus size={16} />} onClick={createCtl.open}>
            Novo Time
          </Button>
        }
      >
        {(teams ?? []).length === 0 ? (
          <EmptyState
            bare
            icon={IconUsers}
            title="Nenhum time cadastrado"
            description="Crie um time e defina sua cobertura de alertas Zabbix."
            size="md"
          />
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Time</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(teams ?? []).map((t) => (
                <Table.Tr key={t.id}>
                  <Table.Td>{t.name}</Table.Td>
                  <Table.Td>
                    <Badge color={t.is_active ? 'teal' : 'gray'}>
                      {t.is_active ? 'ativo' : 'inativo'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconUserPlus size={14} />}
                        onClick={() => {
                          setMemberForId(t.id)
                          setMemberUserId('')
                        }}
                      >
                        Membro
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          setCoverageForId(t.id)
                          setCv((c) => ({ ...c, client: '' }))
                        }}
                      >
                        Cobertura
                      </Button>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        title="Desativar time"
                        loading={m.deactivate.isPending}
                        onClick={() => m.deactivate.mutate(t.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </DataCard>

      {/* Modal: criar time */}
      <Modal opened={createOpen} onClose={createCtl.close} title="Novo Time de alerta">
        <Stack gap="sm">
          <TextInput
            label="Nome"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder="DBA, Squad MDM..."
          />
          <Button
            loading={m.create.isPending}
            disabled={!name.trim()}
            onClick={() =>
              m.create.mutate(
                { name: name.trim() },
                {
                  onSuccess: () => {
                    setName('')
                    createCtl.close()
                  },
                },
              )
            }
          >
            Criar
          </Button>
        </Stack>
      </Modal>

      {/* Modal: definir cobertura */}
      <Modal
        opened={coverageForId !== null}
        onClose={() => setCoverageForId(null)}
        title="Definir cobertura"
      >
        <Stack gap="sm">
          <TextInput
            label="Cliente (prefixo de host group)"
            value={cv.client}
            placeholder="MDM-CIELO"
            onChange={(e) => setCv({ ...cv, client: e.currentTarget.value })}
          />
          <Select
            label="Ambiente"
            data={ENVS}
            value={cv.environment}
            onChange={(v) => setCv({ ...cv, environment: v ?? 'PRD' })}
          />
          <Select
            label="Severidade mínima"
            data={SEV_OPTIONS}
            value={cv.severity_min}
            onChange={(v) => setCv({ ...cv, severity_min: v ?? '2' })}
          />
          <MultiSelect
            label="Canais"
            data={['feed', 'webpush']}
            value={cv.channels}
            onChange={(v) => setCv({ ...cv, channels: v })}
          />
          <Switch
            label="Notificar resolução (✅)"
            checked={cv.notify_resolved}
            onChange={(e) => setCv({ ...cv, notify_resolved: e.currentTarget.checked })}
          />
          <Button
            loading={m.setCoverage.isPending}
            disabled={!cv.client.trim() || coverageForId === null}
            onClick={() =>
              m.setCoverage.mutate(
                {
                  id: coverageForId!,
                  c: {
                    client: cv.client.trim(),
                    environment: cv.environment,
                    severity_min: Number(cv.severity_min),
                    channels: cv.channels,
                    notify_resolved: cv.notify_resolved,
                  },
                },
                { onSuccess: () => setCoverageForId(null) },
              )
            }
          >
            Salvar cobertura
          </Button>
        </Stack>
      </Modal>

      {/* Modal: adicionar membro */}
      <Modal
        opened={memberForId !== null}
        onClose={() => setMemberForId(null)}
        title="Adicionar membro ao time"
      >
        <Stack gap="sm">
          {userOptions.length > 0 ? (
            <Select
              label="Usuário"
              placeholder="Selecione um usuário"
              data={userOptions}
              searchable
              value={memberUserId !== '' ? String(memberUserId) : null}
              onChange={(v) => setMemberUserId(v ? Number(v) : '')}
            />
          ) : (
            <NumberInput
              label="User ID"
              placeholder="Ex: 42"
              value={memberUserId}
              onChange={(v) => setMemberUserId(typeof v === 'number' ? v : '')}
              min={1}
            />
          )}
          <Button
            loading={m.addMember.isPending}
            disabled={memberUserId === '' || memberForId === null}
            onClick={() =>
              m.addMember.mutate(
                { id: memberForId!, userId: Number(memberUserId) },
                {
                  onSuccess: () => {
                    setMemberForId(null)
                    setMemberUserId('')
                  },
                },
              )
            }
          >
            Adicionar
          </Button>
        </Stack>
      </Modal>
    </Stack>
  )
}
